import {lazy, Suspense, useEffect, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {usePageText} from '../context/page-text-context';
import {useRepair} from '../context/repair-context';
import {EnRouteTitle} from './en-route-title';
import Event from './event';
import {Loading} from './loading';
import {AppointmentSummary} from './appointment-summary';

const CanceledRepair = lazy(() => import('./canceled-repair'));
const CompletedSummary = lazy(() => import('./completed-summary'));
const RequestedRepair = lazy(() => import('./requested-repair'));
const TechnicianDetails = lazy(() => import('./technician-details'));
const TrackerMap = lazy(() => import('./tracker-map'));

const RepairTypes = {
  PENDING_SCHEDULE: 'requestSchedule',
  CONFIRMED: 'repairConfirmation',
  PENDING: 'repairPending',
  ACCEPTED: 'technicianAccept',
  IN_PROGRESS: 'repairProgress',
  COMPLETED: 'repairComplete',
  CANCELLED: 'repairCanceled',
};

/* Creates a list of repair events based on types (pending, requested, accepted, etc) */
const createEventQueue = ({repair, trackingPageText}) => {
  return repairEvents.filter((getRepairEvent) => getRepairEvent({repair, trackingPageText}).shouldShow);
};

/* Repair Tracking Page */
export const RepairTracking = () => {
  const {repair, dynamicContent} = useRepair();
  const pageText = usePageText();
  const {defaultRating = 5} = useParams();
  const {serviceOption} = repair;
  const {showPostCancellationOptions, groupCancellationMessage} = dynamicContent;
  const trackingPageText = pageText.tracking;
  const shouldResetEvents = useRef(true);
  const inStoreRepair = serviceOption === 'IN_STORE';
  useEffect(() => {
    shouldResetEvents.current = false;
  }, [repair]);

  // creates list of repair events to display the history on tracking page
  const eventQueue = createEventQueue({
    repair,
    trackingPageText,
  }).map((eventQueueItem) =>
    eventQueueItem({
      repair: {...repair, defaultRating, inStoreRepair},
      location: repair.lastLocation,
      showPostCancellationOptions,
      groupCancellationMessage,
      trackingPageText,
    })
  );

  return (
    <Suspense
      fallback={
        <div className={styles.loadingContainer}>
          <Loading />
        </div>
      }>
      <div className="flex flex-col px-6 py-8 xl:grid xl:grid-cols-5 xl:px-40 xl:py-4 gap-4 md:px-36">
        <div className="col-span-2">
          <AppointmentSummary />
        </div>
        <div className="col-span-3">
          {repair &&
            eventQueue.map((event, i) => {
              return event.shouldShow ? (
                <Event
                  active={i === eventQueue.length - 1}
                  danger={event.icon === 'cancel'}
                  defaultRating={defaultRating}
                  key={event.title}
                  repair={repair}
                  shouldReset={shouldResetEvents.current}
                  {...event}
                />
              ) : null;
            })}
        </div>
      </div>
    </Suspense>
  );
};

/* List of repair events (function that takes in params and returns an object) */
const repairEvents = [
  /* Repair - Pending Schedule */
  ({repair, trackingPageText}) => {
    const type = RepairTypes.PENDING_SCHEDULE;
    return {
      title: trackingPageText[type],
      icon: 'check',
      shouldShow: !repair.scheduledWindowStart,
    };
  },

  /* Repair - Confirmation */
  ({repair, trackingPageText}) => {
    const type = RepairTypes.CONFIRMED;
    return {
      title: trackingPageText[type]?.confirmed || '',
      icon: 'check',
      shouldShow: !!repair.scheduledWindowStart,
    };
  },

  /* Repair - Pending */
  ({repair: {opportunities: {pending = 0} = {}, inStoreRepair = false}, trackingPageText}) => {
    const type = RepairTypes.PENDING;
    return {
      title: !inStoreRepair ? trackingPageText[type]?.looking : trackingPageText[type]?.confirming,
      icon: 'search',
      shouldShow: pending > 0,
    };
  },
  /* Repair - Store/Technician Accepted */
  ({repair, trackingPageText}) => {
    const {
      device = 'device',
      technician = {},
      technicianName = '',
      workflowServiceType = '',
      state,
      inStoreRepair = false,
    } = repair;
    const type = RepairTypes.ACCEPTED;
    const title = inStoreRepair
      ? `${repair.storeName} ${trackingPageText[type]?.inStore}`
      : `${trackingPageText[type]?.technician?.accept} ${technicianName} ${trackingPageText[type]?.technician?.helping}`;
    return {
      title,
      icon: 'technician',
      shouldShow: !!technicianName && !!repair.scheduledWindowStart,
      subview: () => {
        return inStoreRepair ? (
          <RequestedRepair {...repair} {...trackingPageText} preciseTimes={repair.customerPreciseTime} />
        ) : (
          <TechnicianDetails
            device={device}
            repairState={state}
            technician={technician}
            workflowServiceType={workflowServiceType}
          />
        );
      },
    };
  },
  /* Repair - Technician En Route */
  ({repair: {positionState = '', state = '', technicianName = '', inStoreRepair, ...repair}}) => {
    return inStoreRepair
      ? {
          shouldShow: false,
        }
      : {
          title: () => <EnRouteTitle repair={repair} />,
          icon: 'enroute',
          shouldShow:
            !!technicianName &&
            (['on_track', 'on_site'].includes(positionState) || ['in_progress', 'completed'].includes(state)),
          subview: () => <TrackerMap repair={repair} />,
          customSubviewStyle: {padding: '0 !important'},
        };
  },

  /* Repair - In-Progress */
  ({repair: {state = ''}, trackingPageText}) => {
    const type = RepairTypes.IN_PROGRESS;
    return {
      title: trackingPageText[type],
      icon: 'clock',
      shouldShow: state === 'in_progress' || ['completed'].includes(state),
    };
  },

  /* Repair - Summary */
  ({repair, trackingPageText}) => {
    const type = RepairTypes.COMPLETED;
    return {
      title: trackingPageText[type],
      icon: 'complete',
      shouldShow: !!repair.completedAt,
      subview: () => (
        <>
          <CompletedSummary
            {...repair}
            {...trackingPageText}
            preciseTimes={repair.customerPreciseTime}
            repairId={repair.id}
          />
        </>
      ),
    };
  },

  /* Repair - Cancelled */
  ({repair: {state = ''}, showPostCancellationOptions, groupCancellationMessage, trackingPageText}) => {
    const type = RepairTypes.CANCELLED;
    return {
      title: trackingPageText[type],
      icon: 'cancel',
      subview: () => (showPostCancellationOptions ? <CanceledRepair /> : groupCancellationMessage),
      shouldShow: state === 'canceled',
    };
  },
];

const styles = {
  loadingContainer: 'h-full w-[5em] mt-[25%] mx-auto',
};

export {repairEvents};
